// src/services/apaleo/city-tax-markers.ts
// Pure helpers for the city-tax automation markers (date formatting,
// operator initials, i18n marker patterns). Extracted verbatim from
// src/services/apaleo.ts (Phase 5 of plans/monolith-migration-plan.md).
//
// Kept separate from city-tax.ts so both modules stay under the 500-line
// file-size ratchet cap; the method bodies are unchanged.

import i18n from "../../i18n";
import type { ToolUser, LogFn } from './types';
import type { ApaleoClient } from './client-contract';
import { errorMessage } from './config';

const part: ThisType<ApaleoClient> = {
_normalizeAuditFilters(
  sources: string[] | string | null,
  createdFrom: string | null,
  createdTo: string | null,
): {
  sourceFilter: string[] | null;
  createdFromTs: Date | null;
  createdToTs: Date | null;
} {
  let sourceFilter: string[] | null = null;
  if (typeof sources === "string" && sources.trim()) {
    sourceFilter = [sources.trim()];
  } else if (Array.isArray(sources)) {
    const cleaned = sources.map((s) => String(s || "").trim()).filter(Boolean);
    if (cleaned.length > 0) sourceFilter = cleaned;
  }

  const parseLocalDayStart = (dateStr: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    // Reject month/day rollovers (new Date normalizes 2026-13-99 silently).
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo - 1, d, 0, 0, 0, 0);
    if (
      Number.isNaN(dt.getTime()) ||
      dt.getFullYear() !== y ||
      dt.getMonth() !== mo - 1 ||
      dt.getDate() !== d
    ) {
      return null;
    }
    return dt;
  };

  let createdFromTs = null;
  let createdToTs = null;
  if (createdFrom) {
    createdFromTs = parseLocalDayStart(createdFrom);
    if (!createdFromTs) {
      throw new Error(
        `createdFrom must be YYYY-MM-DD (got "${createdFrom}")`,
      );
    }
  }
  if (createdTo) {
    createdToTs = parseLocalDayStart(createdTo);
    if (!createdToTs) {
      throw new Error(
        `createdTo must be YYYY-MM-DD (got "${createdTo}")`,
      );
    }
  }
  if (createdFromTs && createdToTs && createdFromTs > createdToTs) {
    throw new Error("createdFrom must be <= createdTo");
  }

  return { sourceFilter, createdFromTs, createdToTs };
},

_localDateIso(input: Date = new Date()) {
  const now = input;
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
},

_localDateGerman(input: Date = new Date()) {
  const now = input;
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${dd}.${mm}.${now.getFullYear().toString().slice(-2)}`;
},

_operatorInitials(user?: ToolUser | null): string {
  const name = String(user?.name ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const fallback = String(user?.id ?? user?.email ?? "").trim();
  return fallback ? fallback.slice(0, 2).toUpperCase() : "";
},

_automationMarkerLine(
  key: string,
  user?: ToolUser | null,
  extraParams: Record<string, unknown> = {},
): string {
  const initials = this._operatorInitials(user);
  const line = i18n.t(key, {
    date: this._localDateGerman(),
    ...extraParams,
  });
  return initials ? `${line} – ${initials}` : line;
},

async _appendReservationComment(
  reservationId: string,
  line: string,
  log: LogFn | null = null,
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Re-fetch instead of trusting the caller's cached object: the comment
    // may have changed between the initial fetch and the amend. Expand
    // param "" = base fields only — `comment` is one of them (the guest
    // profile reads it without any expand).
    const fresh = await this.fetchReservationById(reservationId, "");
    const existing = typeof fresh?.comment === "string" ? fresh.comment : "";
    if (existing && existing.includes(line)) {
      if (log) log("log", `Comment on ${reservationId} already notes this automation — not writing again`);
      return { ok: true };
    }
    const nextComment = existing ? `${existing}\n${line}` : line;
    await this.updateReservation(reservationId, [
      { op: existing ? "replace" : "add", path: "/comment", value: nextComment },
    ]);
    if (log) log("log", `Automation note appended to comment of ${reservationId}`);
    return { ok: true };
  } catch (e) {
    const msg = errorMessage(e);
    if (log) log("warn", `Failed to append automation note to comment of ${reservationId}: ${msg}`);
    return { ok: false, error: msg };
  }
},

async appendReservationComment(
  reservationId: string,
  line: string,
): Promise<{ ok: boolean; error?: string }> {
  return this._appendReservationComment(reservationId, line, null);
},

_automationMarkerPatterns() {
  const DATE_SENTINEL = "__ADAPTHUB_DATE__";
  const REASON_SENTINEL = "__ADAPTHUB_REASON__";
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const keys = [
    "recalcCityTax.automationComment",
    "recalcCityTax.automationCommentSplit",
    "recalcCityTax.automationCommentSplitNoBreakfast",
  ];
  // Cover every configured language, not just the currently active one:
  // the marker is written in the RUNNER'S language, the check may run later
  // under a different UI language.
  const languages: Array<string | undefined> = Array.isArray(i18n.languages) && i18n.languages.length > 0
    ? Array.from(new Set([...i18n.languages, "de", "en"]))
    : ["de", "en"];
  const patterns: RegExp[] = [];
  const seen = new Set<string>();
  keys.forEach((key) => {
    languages.forEach((lng) => {
      const template = i18n.t(key, {
        ...(lng ? { lng } : {}),
        date: DATE_SENTINEL,
        reason: REASON_SENTINEL,
      });
      if (typeof template !== "string" || template === key) return;
      // A template without the date placeholder cannot produce a reliable
      // pattern — skip it rather than risk a wrong match.
      if (!template.includes(DATE_SENTINEL)) return;
      const source = escapeRegExp(template)
        .split(DATE_SENTINEL).join("[\\s\\S]*?")
        .split(REASON_SENTINEL).join("[\\s\\S]*?");
      if (seen.has(source)) return;
      seen.add(source);
      patterns.push(new RegExp(source));
    });
  });
  return patterns;
},

_hasAutomationMarker(comment: unknown) {
  if (typeof comment !== "string" || comment.trim() === "") return false;
  return this._automationMarkerPatterns().some((re) => re.test(comment));
},
};

export default part;
